export type RetellCallsMtd = {
  answered: number;
  ended: number;
  intakeRatePct: number;
  intakes: number;
  scheduledCallsGoal: number | null;
};

export type RetellCallsCore = {
  bookings: number;
  ended: number;
  goalPct: number;
  inbound: number;
  intakeRatePct: number;
  intakes: number;
  live: number;
  machineOverrides: number;
  medianDurationSec: number;
  monthlyVolumeGoal: number;
  mtd: RetellCallsMtd | null;
  outbound: number;
  transfers: number;
  uniqueNumbers: number;
  vmIvr: number;
  vmRaw: number;
};

export type RetellCallsCategory = {
  calls: number;
  category: string;
};

export type RetellCallsDisconnection = {
  calls: number;
  reason: string;
};

export type RetellCallsHourlyBucket = {
  calls: number;
  hour: number;
  intakes: number;
};

export type RetellCallsTrendPoint = {
  answered: number;
  calls: number;
  day: string;
  intakeRate: number;
  intakes: number;
  transfers: number;
};

export type RetellCallsTransferReportRow = {
  completedTransfers: number;
  connected: number;
  connectedRatePct: number;
  connectedRequestRatePct: number;
  day: string;
  dialpadPickedUp: number;
  markedTransferRatePct: number;
  markedTransferred: number;
  matchedDialpadCalls: number;
  phoneTimeMatches?: number;
  pickupRatePct: number;
  pipelinePickedUp: number;
  pipelinePickupRatePct: number;
  requestRatePct: number;
  totalRetellCalls: number;
  transferRequests: number;
  transferredTimeMatches?: number;
};

export type RetellCallsCareSpaceAgeCohort = {
  answerRatePct: number;
  answeredCalls: number;
  intakesScheduled: number;
  key: string;
  label: string;
  maxAgeDays?: number | null;
  minAgeDays?: number | null;
  placedCalls: number;
  scheduledAnswerRatePct: number;
  scheduledPlacedRatePct: number;
  sortOrder?: number;
  unmatched: boolean;
};

export type RetellCallsClinicBreakdownRow = {
  clinic: string;
  humanPickups: number;
  intakePickupRatePct: number;
  intakesScheduled: number;
  key: string;
  pickupRatePct: number;
  retellCalls: number;
  unmatched: boolean;
};

export type RetellCallsLongestCall = {
  category: string;
  durationSec: number;
  reason: string;
};

export type RetellCallsLongNonIntake = {
  categories: RetellCallsCategory[];
  total: number;
};

export type RetellCallsDefinitions = {
  careSpaceAgeCohorts?: Record<string, string>;
  clinicBreakdown?: Record<string, string>;
  transferReport?: {
    connectedThresholdSeconds?: number;
    lookbackDays?: number;
    pickupThresholdSeconds?: number;
    [key: string]: unknown;
  };
};

export type RetellCallsSummaryResponse = {
  categories: RetellCallsCategory[];
  contactClasses?: Array<{ calls: number; class: string }>;
  core: RetellCallsCore;
  disconnections: RetellCallsDisconnection[];
  end: string;
  hourly: RetellCallsHourlyBucket[];
  longNonIntake: RetellCallsLongNonIntake;
  longestCall: RetellCallsLongestCall | null;
  start: string;
  trend: RetellCallsTrendPoint[];
};

export type RetellCallsTransferReportResponse = {
  definition: NonNullable<RetellCallsDefinitions["transferReport"]>;
  end: string;
  transferReport: RetellCallsTransferReportRow[];
};

export type RetellCallsCareSpaceBreakdownResponse = {
  careSpaceAgeCohorts: RetellCallsCareSpaceAgeCohort[];
  clinicBreakdown: RetellCallsClinicBreakdownRow[];
  definitions: Pick<RetellCallsDefinitions, "careSpaceAgeCohorts" | "clinicBreakdown">;
  end: string;
  start: string;
};

export type RetellCallsSnapshotResponse = RetellCallsSummaryResponse &
  RetellCallsCareSpaceBreakdownResponse & {
    definitions: RetellCallsDefinitions;
    transferReport: RetellCallsTransferReportRow[];
  };

export type RetellCallsSnapshotParams = {
  end: string;
  start: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasFieldsOfType(
  value: unknown,
  fields: readonly string[],
  type: "boolean" | "number" | "string",
): value is Record<string, unknown> {
  return isRecord(value) && fields.every((field) => typeof value[field] === type);
}

function isArrayOf<T>(value: unknown, guard: (item: unknown) => item is T): value is T[] {
  return Array.isArray(value) && value.every(guard);
}

function isCategory(value: unknown): value is RetellCallsCategory {
  return hasFieldsOfType(value, ["calls"], "number") && typeof value.category === "string";
}

function isCore(value: unknown): value is RetellCallsCore {
  if (
    !hasFieldsOfType(
      value,
      [
        "bookings",
        "ended",
        "goalPct",
        "inbound",
        "intakeRatePct",
        "intakes",
        "live",
        "machineOverrides",
        "medianDurationSec",
        "monthlyVolumeGoal",
        "outbound",
        "transfers",
        "uniqueNumbers",
        "vmIvr",
        "vmRaw",
      ],
      "number",
    )
  ) {
    return false;
  }
  return (
    value.mtd === null ||
    (hasFieldsOfType(value.mtd, ["answered", "ended", "intakeRatePct", "intakes"], "number") &&
      (typeof value.mtd.scheduledCallsGoal === "number" || value.mtd.scheduledCallsGoal === null))
  );
}

function isTransferReportRow(value: unknown): value is RetellCallsTransferReportRow {
  return (
    hasFieldsOfType(
      value,
      [
        "completedTransfers",
        "connected",
        "connectedRatePct",
        "connectedRequestRatePct",
        "dialpadPickedUp",
        "markedTransferRatePct",
        "markedTransferred",
        "matchedDialpadCalls",
        "pickupRatePct",
        "pipelinePickedUp",
        "pipelinePickupRatePct",
        "requestRatePct",
        "totalRetellCalls",
        "transferRequests",
      ],
      "number",
    ) &&
    typeof value.day === "string" &&
    (value.phoneTimeMatches === undefined || typeof value.phoneTimeMatches === "number") &&
    (value.transferredTimeMatches === undefined || typeof value.transferredTimeMatches === "number")
  );
}

function isCareSpaceAgeCohort(value: unknown): value is RetellCallsCareSpaceAgeCohort {
  return (
    hasFieldsOfType(
      value,
      [
        "answerRatePct",
        "answeredCalls",
        "intakesScheduled",
        "placedCalls",
        "scheduledAnswerRatePct",
        "scheduledPlacedRatePct",
      ],
      "number",
    ) &&
    hasFieldsOfType(value, ["key", "label"], "string") &&
    typeof value.unmatched === "boolean" &&
    (value.maxAgeDays === undefined ||
      value.maxAgeDays === null ||
      typeof value.maxAgeDays === "number") &&
    (value.minAgeDays === undefined ||
      value.minAgeDays === null ||
      typeof value.minAgeDays === "number") &&
    (value.sortOrder === undefined || typeof value.sortOrder === "number")
  );
}

function isClinicBreakdownRow(value: unknown): value is RetellCallsClinicBreakdownRow {
  return (
    hasFieldsOfType(
      value,
      ["humanPickups", "intakePickupRatePct", "intakesScheduled", "pickupRatePct", "retellCalls"],
      "number",
    ) &&
    hasFieldsOfType(value, ["clinic", "key"], "string") &&
    typeof value.unmatched === "boolean"
  );
}

export function isRetellCallsSnapshotResponse(
  value: unknown,
): value is RetellCallsSnapshotResponse {
  if (!isRecord(value) || !isRetellCallsSummaryResponse(value)) return false;
  const snapshot = value as RetellCallsSummaryResponse & Record<string, unknown>;
  return (
    isRecord(snapshot.definitions) &&
    isArrayOf(snapshot.transferReport, isTransferReportRow) &&
    isArrayOf(snapshot.careSpaceAgeCohorts, isCareSpaceAgeCohort) &&
    isArrayOf(snapshot.clinicBreakdown, isClinicBreakdownRow)
  );
}

export function isRetellCallsSummaryResponse(value: unknown): value is RetellCallsSummaryResponse {
  if (!isRecord(value) || !isCore(value.core)) return false;
  return (
    typeof value.start === "string" &&
    typeof value.end === "string" &&
    isArrayOf(value.categories, isCategory) &&
    isArrayOf(
      value.disconnections,
      (item): item is RetellCallsDisconnection =>
        hasFieldsOfType(item, ["calls"], "number") && typeof item.reason === "string",
    ) &&
    isArrayOf(value.hourly, (item): item is RetellCallsHourlyBucket =>
      hasFieldsOfType(item, ["calls", "hour", "intakes"], "number"),
    ) &&
    isArrayOf(
      value.trend,
      (item): item is RetellCallsTrendPoint =>
        hasFieldsOfType(
          item,
          ["answered", "calls", "intakeRate", "intakes", "transfers"],
          "number",
        ) && typeof item.day === "string",
    ) &&
    (value.longestCall === null ||
      (hasFieldsOfType(value.longestCall, ["durationSec"], "number") &&
        hasFieldsOfType(value.longestCall, ["category", "reason"], "string"))) &&
    isRecord(value.longNonIntake) &&
    typeof value.longNonIntake.total === "number" &&
    isArrayOf(value.longNonIntake.categories, isCategory) &&
    (value.contactClasses === undefined ||
      isArrayOf(
        value.contactClasses,
        (item): item is { calls: number; class: string } =>
          hasFieldsOfType(item, ["calls"], "number") && typeof item.class === "string",
      ))
  );
}

export function isRetellCallsTransferReportResponse(
  value: unknown,
): value is RetellCallsTransferReportResponse {
  return (
    isRecord(value) &&
    typeof value.end === "string" &&
    isRecord(value.definition) &&
    isArrayOf(value.transferReport, isTransferReportRow)
  );
}

export function isRetellCallsCareSpaceBreakdownResponse(
  value: unknown,
): value is RetellCallsCareSpaceBreakdownResponse {
  return (
    isRecord(value) &&
    typeof value.start === "string" &&
    typeof value.end === "string" &&
    isRecord(value.definitions) &&
    isArrayOf(value.careSpaceAgeCohorts, isCareSpaceAgeCohort) &&
    isArrayOf(value.clinicBreakdown, isClinicBreakdownRow)
  );
}

/** Column help copy used by the four Retell definition tables (parity guard). */
export const RETELL_TABLE_COLUMN_DEFINITIONS = {
  pipeline: [
    "The day these calls happened.",
    "Calls that Retell sent on to the call center.",
    "Calls the call center answered for at least {pickup} seconds.",
    "The share of completed calls answered for at least {pickup} seconds.",
  ],
  journey: [
    "The day these calls happened.",
    "All Retell calls that ended that day.",
    "Calls where the person asked to talk to the call center.",
    "Requests we linked to a call at the call center.",
    "Linked calls our phone system says were sent over.",
    "Sent calls that someone at the call center answered.",
    "Sent calls with at least {connected} seconds of talk time.",
    "The share of Retell calls where the person asked for the call center.",
    "The share of requests our phone system says were sent over.",
    "The share of sent calls someone at the call center answered.",
    "The share of sent calls with at least {connected} seconds of talk time.",
    "The share of requests with at least {connected} seconds of talk time.",
  ],
  ageCohort: [
    "How many days had passed since the CareSpace was made.",
    "Calls Retell made for CareSpaces this old.",
    "Placed calls that a person answered.",
    "Answered calls where an intake was set up.",
    "The share of placed calls that a person answered.",
    "The share of answered calls where an intake was set up.",
    "The share of placed calls where an intake was set up.",
  ],
  clinic: [
    "The clinic tied to the calls in this row.",
    "Calls Retell made for this clinic.",
    "Calls that a person answered.",
    "The share of Retell calls that a person answered.",
    "Answered calls where an intake was set up.",
    "The share of answered calls where an intake was set up.",
  ],
} as const;
